import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const HeroSection = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // ฟังก์ชันแปลงสภาพอากาศเป็น icon
    const getWeatherIcon = (main) => {
      const icons = {
        'Clear': '☀️',
        'Clouds': '☁️',
        'Rain': '🌧️',
        'Drizzle': '🌦️',
        'Thunderstorm': '⛈️',
        'Snow': '❄️',
        'Mist': '🌫️',
        'Fog': '🌫️'
      };
      return icons[main] || '☀️';
    };

    // ฟังก์ชันดึงข้อมูลอุณหภูมิ
    const fetchWeather = async (lat, lon) => {
      try {
        // ใช้ OpenWeatherMap API (ต้องมี API key)
        // สำหรับ demo ใช้ free tier: https://openweathermap.org/api
        const API_KEY = import.meta.env.VITE_OPENWEATHER_API_KEY || 'demo';
        
        // ถ้าไม่มี API key ให้ใช้ข้อมูล mock
        if (API_KEY === 'demo') {
          // ใช้ข้อมูล mock สำหรับอำเภอเทิง
          setWeather({
            temp: 28,
            feelsLike: 30,
            description: 'ท้องฟ้าแจ่มใส',
            icon: '☀️',
            humidity: 65,
            location: 'อำเภอเทิง'
          });
          setLoading(false);
          return;
        }

        const response = await fetch(
          `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&appid=${API_KEY}&units=metric&lang=th`
        );
        
        if (!response.ok) throw new Error('ไม่สามารถดึงข้อมูลอุณหภูมิได้');
        
        const data = await response.json();
        setWeather({
          temp: Math.round(data.main.temp),
          feelsLike: Math.round(data.main.feels_like),
          description: data.weather[0].description,
          icon: getWeatherIcon(data.weather[0].main),
          humidity: data.main.humidity,
          location: 'อำเภอเทิง'
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching weather:', err);
        // ใช้ข้อมูล mock เมื่อเกิด error
        setWeather({
          temp: 28,
          feelsLike: 30,
          description: 'ท้องฟ้าแจ่มใส',
          icon: '☀️',
          humidity: 65,
          location: 'อำเภอเทิง'
        });
        setLoading(false);
      }
    };

    // ดึงพิกัดจากผู้ใช้
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          fetchWeather(latitude, longitude);
        },
        (err) => {
          console.error('Geolocation error:', err);
          // ใช้พิกัดของอำเภอเทิงเป็นค่า default
          // อำเภอเทิง จังหวัดเชียงราย: 19.7, 100.2
          fetchWeather(19.7, 100.2);
        }
      );
    } else {
      // ถ้าเบราว์เซอร์ไม่รองรับ geolocation ใช้พิกัด default
      fetchWeather(19.7, 100.2);
    }
  }, []);

  return (
    <section className="relative text-primary-foreground py-16 lg:py-24 overflow-hidden">
      {/* Weather Widget - Top Right */}
      <motion.div
        className="absolute top-4 right-4 lg:top-6 lg:right-6 z-10"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <div className="bg-white/20 backdrop-blur-md rounded-2xl p-4 lg:p-5 shadow-2xl border border-white/30">
          {loading ? (
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-white/20 animate-pulse"></div>
              <div className="space-y-2">
                <div className="h-4 w-20 bg-white/20 rounded animate-pulse"></div>
                <div className="h-3 w-16 bg-white/20 rounded animate-pulse"></div>
              </div>
            </div>
          ) : weather ? (
            <div className="flex items-center space-x-3 lg:space-x-4">
              <div className="text-4xl lg:text-5xl">{weather.icon}</div>
              <div className="flex flex-col">
                <div className="flex items-baseline space-x-1">
                  <span className="text-2xl lg:text-3xl font-bold text-white">
                    {weather.temp}
                  </span>
                  <span className="text-lg lg:text-xl text-white/80">°C</span>
                </div>
                <div className="text-xs lg:text-sm text-white/90 font-medium">
                  {weather.description}
                </div>
                <div className="text-xs text-white/70 mt-1">
                  {weather.location}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-3">
              <div className="text-4xl">☀️</div>
              <div className="text-sm text-white/80">ไม่สามารถโหลดข้อมูลได้</div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Animated background (Ken Burns) */}
      <motion.img 
        src="/assets/images/bgnew.png"
        alt="พื้นหลังอำเภอเทิง"
        className="absolute inset-0 w-full h-full object-cover blur-[1.5px]"
        initial={{ scale: 1.05, x: 0, y: 0 }}
        animate={{ scale: 1.15, x: 10, y: 6 }}
        transition={{ duration: 28, ease: 'easeInOut', repeat: Infinity, repeatType: 'mirror' }}
        style={{ willChange: 'transform' }}
      />
      <div className="absolute inset-0 bg-black/30"></div>
      {/* Bottom gradient overlay for readability */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent" />
      {/* Foreground parallax layer (flowers) */}
      <motion.div
        className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none"
        style={{ 
          backgroundImage: 'url("/assets/images/bgnew.png")',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center bottom',
          WebkitMaskImage: 'linear-gradient(to top, black 55%, transparent 100%)',
          maskImage: 'linear-gradient(to top, black 55%, transparent 100%)',
          opacity: 0.9,
          willChange: 'transform'
        }}
        animate={{ y: [-4, 0, -4], x: [0, -6, 0] }}
        transition={{ duration: 14, ease: 'easeInOut', repeat: Infinity }}
      />
      <div className="relative max-w-7xl mx-auto px-4 lg:px-6">
        <div className="text-center">
          {/* Government Seal */}
          <div className="flex justify-center mb-8">
            <div className="w-24 h-24 lg:w-32 lg:h-32 rounded-full flex items-center justify-center shadow-government-lg bg-transparent">
              <motion.img 
                src="/assets/images/b822850b984eba9df2620697399b50075bab49cf64ce35210033003315257900-1757413105446.png" 
                alt="ตราสิงที่ทำการปกครองอำเภอเทิง" 
                className="w-16 h-16 lg:w-20 lg:h-20 object-contain"
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 6, ease: 'easeInOut', repeat: Infinity }}
              />
            </div>
          </div>

          {/* Main Title */}
          <h1 className="text-4xl lg:text-6xl font-bold mb-4 leading-tight tracking-tight">
            {(() => {
              // แยกตัวอักษรที่รองรับภาษาไทย (รวมเครื่องหมายวรรณยุกต์)
              const text = "ที่ทำการปกครองอำเภอเทิง";
              // ใช้ Intl.Segmenter เพื่อแยก grapheme clusters (ตัวอักษร + เครื่องหมายวรรณยุกต์)
              if (typeof Intl !== 'undefined' && Intl.Segmenter) {
                const segmenter = new Intl.Segmenter('th', { granularity: 'grapheme' });
                const segments = Array.from(segmenter.segment(text), s => s.segment);
                return segments.map((ch, i) => (
                  <motion.span
                    key={`title-${i}-${ch}`}
                    style={{ display: 'inline-block' }}
                    initial={{ y: 12, opacity: 0 }}
                    animate={{ y: [12, -4, 0], opacity: 1 }}
                    transition={{ delay: 0.02 * i, duration: 0.9, ease: 'easeOut', repeatDelay: 2.2, repeat: Infinity }}
                  >
                    {ch}
                  </motion.span>
                ));
              } else {
                // Fallback: ใช้วิธีแยกแบบง่าย (อาจไม่รองรับเครื่องหมายวรรณยุกต์ทั้งหมด)
                return text.split('').map((ch, i) => (
                  <motion.span
                    key={`title-${i}-${ch}`}
                    style={{ display: 'inline-block' }}
                    initial={{ y: 12, opacity: 0 }}
                    animate={{ y: [12, -4, 0], opacity: 1 }}
                    transition={{ delay: 0.02 * i, duration: 0.9, ease: 'easeOut', repeatDelay: 2.2, repeat: Infinity }}
                  >
                    {ch}
                  </motion.span>
                ));
              }
            })()}
          </h1>
          <p className="text-xl lg:text-2xl opacity-90 mb-10 max-w-3xl mx-auto">
            {"Thoeng District Office".split("").map((ch, i) => (
              <motion.span
                key={`sub-${i}-${ch}`}
                style={{ display: 'inline-block' }}
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: [10, -3, 0], opacity: 1 }}
                transition={{ delay: 0.015 * i + 0.2, duration: 0.8, ease: 'easeOut', repeatDelay: 2.4, repeat: Infinity }}
              >
                {ch}
              </motion.span>
            ))}
          </p>

          

          {/* Vision Statement */}
          <motion.div
            className="bg-white/10 backdrop-blur-sm rounded-lg p-6 lg:p-8 max-w-4xl mx-auto mb-8"
            initial={{ y: 36, opacity: 0, scale: 0.96 }}
            whileInView={{ y: [36, -6, 0], opacity: 1, scale: [0.96, 1.03, 1] }}
            viewport={{ once: true, margin: '-10% 0px -10% 0px' }}
            transition={{ duration: 0.7, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <h2 className="text-xl lg:text-2xl font-semibold mb-4 flex items-center justify-center text-[#D4A017]">
              <Icon name="Eye" size={24} className="mr-3" />
              วิสัยทัศน์
            </h2>
            <p className="text-base lg:text-lg leading-relaxed">
              "อำเภอเทิงเป็นอำเภอน่าอยู่ มีเศรษฐกิจเข้มแข็ง ประชาชนมีคุณภาพชีวิตที่ดี 
              บนพื้นฐานของความเป็นเมืองท่องเที่ยววัฒนธรรม และเกษตรอินทรีย์ยั่งยืน"
            </p>
          </motion.div>

          {/* Mission Statement */}
          <motion.div
            className="bg-white/10 backdrop-blur-sm rounded-lg p-6 lg:p-8 max-w-4xl mx-auto mb-8"
            initial={{ y: 36, opacity: 0, scale: 0.96 }}
            whileInView={{ y: [36, -6, 0], opacity: 1, scale: [0.96, 1.03, 1] }}
            viewport={{ once: true, margin: '-10% 0px -10% 0px' }}
            transition={{ duration: 0.7, ease: [0.34, 1.56, 0.64, 1], delay: 0.06 }}
          >
            <h2 className="text-xl lg:text-2xl font-semibold mb-4 flex items-center justify-center text-[#D4A017]">
              <Icon name="Target" size={24} className="mr-3" />
              พันธกิจ
            </h2>
            <p className="text-base lg:text-lg leading-relaxed">
              "ส่งเสริมการท่องเที่ยวเชิงวัฒนธรรม พัฒนาเกษตรอินทรีย์ ยกระดับคุณภาพชีวิตประชาชน 
              และให้บริการประชาชนด้วยความรวดเร็ว โปร่งใส เป็นธรรม"
            </p>
          </motion.div>

          {/* Motto Section (moved to bottom) */}
          <motion.div
            className="bg-white/10 backdrop-blur-sm rounded-lg p-6 lg:p-8 max-w-4xl mx-auto"
            initial={{ y: 36, opacity: 0, scale: 0.96 }}
            whileInView={{ y: [36, -6, 0], opacity: 1, scale: [0.96, 1.03, 1] }}
            viewport={{ once: true, margin: '-10% 0px -10% 0px' }}
            transition={{ duration: 0.7, ease: [0.34, 1.56, 0.64, 1], delay: 0.12 }}
          >
            <h2 className="text-xl lg:text-2xl font-semibold mb-4 flex items-center justify-center text-[#D4A017]">
              <Icon name="Quote" size={24} className="mr-3" />
              คำขวัญอำเภอเทิง
            </h2>
            <p className="text-base lg:text-lg leading-relaxed whitespace-pre-line">
              พระธาตุจอมจ้อคู่บ้าน  น้ำลาว หงาว อิงคู่เมือง  ภูชี้ฟ้าลือเลื่อง  งามประเทืองดอกเสี้ยวบาน
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;